import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';

interface PaginationPage {
  pageNum: number;
  isActive: boolean;
}

const calculatePaginationPages = (
  currentPage: number,
  totalPages: number
): PaginationPage[] => {
  // Calculate the range of pages to show around current page
  const showFirstPage = currentPage > 3;
  const showLastPage = currentPage < totalPages - 2;

  // Determine the start and end of the middle range
  // Try to show 4 pages in the middle when possible
  let startPage = Math.max(1, currentPage - 2);
  let endPage = Math.min(totalPages, currentPage + 2);

  // If we have space for 4 pages, expand the range
  const availableSpace =
    totalPages - (showFirstPage ? 1 : 0) - (showLastPage ? 1 : 0);
  if (availableSpace >= 4) {
    // Center the 4 pages around current page
    startPage = Math.max(1, currentPage - 1);
    endPage = Math.min(totalPages, startPage + 3);

    // Adjust if we're near the end
    if (endPage === totalPages && startPage > 1) {
      startPage = Math.max(1, totalPages - 3);
    }
  }

  // Adjust range to avoid duplicates with first/last page
  if (showFirstPage && startPage <= 1) {
    startPage = 2;
  }
  if (showLastPage && endPage >= totalPages) {
    endPage = totalPages - 1;
  }

  // Generate the middle pages
  const middlePages: PaginationPage[] = [];
  for (let pageNum = startPage; pageNum <= endPage; pageNum++) {
    // Skip if this page would duplicate first or last page
    if (showFirstPage && pageNum === 1) continue;
    if (showLastPage && pageNum === totalPages) continue;

    middlePages.push({
      pageNum,
      isActive: currentPage === pageNum,
    });
  }

  return middlePages;
};

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalLoans: number;
  pageSize: number;
  hasNext: boolean;
  hasPrev: boolean;
  hasActiveFilters: boolean;
  onPageChange: (page: number) => void;
}

export const CommonPagination = ({
  currentPage,
  totalPages,
  totalLoans,
  pageSize,
  hasNext,
  hasPrev,
  hasActiveFilters,
  onPageChange,
}: PaginationProps) => {
  if (totalPages <= 1) return null;
  const middlePages = calculatePaginationPages(currentPage, totalPages);
  return (
    <div
      className="flex items-center justify-between mt-6"
      data-test-id="pagination-section"
    >
      <p
        className="text-sm text-gray-700 text-nowrap"
        data-test-id="pagination-info"
      >
        {`Showing ${(currentPage - 1) * pageSize + 1} to ${Math.min(currentPage * pageSize, totalLoans)} of ${totalLoans} loans` +
          (hasActiveFilters ? ` (filtered from ${totalLoans} total)` : '')}
      </p>
      <Pagination className="justify-end" data-test-id="pagination-controls">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              className={
                !hasPrev ? 'pointer-events-none opacity-50' : 'cursor-pointer'
              }
              data-test-id="pagination-previous"
            />
          </PaginationItem>

          {/* Show first page */}
          {currentPage > 3 && (
            <>
              <PaginationItem>
                <PaginationLink
                  onClick={() => onPageChange(1)}
                  data-test-id="pagination-page-1"
                >
                  1
                </PaginationLink>
              </PaginationItem>
              {currentPage > 4 && (
                <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>
              )}
            </>
          )}

          {/* Show pages around current page */}
          {middlePages.map(({ pageNum, isActive }) => (
            <PaginationItem key={pageNum}>
              <PaginationLink
                onClick={() => onPageChange(pageNum)}
                isActive={isActive}
                className="cursor-pointer"
                data-test-id={`pagination-page-${pageNum}`}
              >
                {pageNum}
              </PaginationLink>
            </PaginationItem>
          ))}

          {/* Show last page */}
          {currentPage < totalPages - 2 && (
            <>
              {currentPage < totalPages - 3 && (
                <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>
              )}
              <PaginationItem>
                <PaginationLink
                  onClick={() => onPageChange(totalPages)}
                  data-test-id={`pagination-page-${totalPages}`}
                >
                  {totalPages}
                </PaginationLink>
              </PaginationItem>
            </>
          )}

          <PaginationItem>
            <PaginationNext
              onClick={() => onPageChange(currentPage + 1)}
              className={
                !hasNext ? 'pointer-events-none opacity-50' : 'cursor-pointer'
              }
              data-test-id="pagination-next"
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};
